import { useRect } from '@studio-freight/hamo'
import cn from 'clsx'

import { useScroll } from 'hooks/use-scroll'
import { Layout } from 'layouts/default'
import { button, useControls } from 'leva'
import { clamp, mapRange } from 'lib/maths'
import { useStore } from 'lib/store'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { useIntersection, useWindowSize } from 'react-use'
import s from '../home/home.module.scss'

const AppearTitle = dynamic(
  () => import('components/appear-title').then((mod) => mod.AppearTitle),
  { ssr: false }
)

const WebGL = dynamic(
  () => import('components/webgl').then(({ WebGL }) => WebGL),
  { ssr: false }
)

export default function Home() {
  const [hasScrolled, setHasScrolled] = useState()
  const zoomRef = useRef(null)
  const [zoomWrapperRect] = useRect()
  const { height: windowHeight } = useWindowSize()

  const [theme, setTheme] = useState('light')
  const lenis = useStore(({ lenis }) => lenis)

  useControls(
    'aryan',
    () => ({
      stop: button(() => {
        lenis.stop()
      }),
      start: button(() => {
        lenis.start()
      }),
    }),
    [lenis]
  )

  useControls(
    'scrollTo',
    () => ({
      immediate: button(() => {
        lenis.scrollTo(30000, { immediate: true })
      }),
      smoothDuration: button(() => {
        lenis.scrollTo(30000, { lock: true, duration: 10 })
      }),
      smooth: button(() => {
        lenis.scrollTo(30000)
      }),
      forceScrollTo: button(() => {
        lenis.scrollTo(30000, { force: true })
      }),
    }),
    [lenis]
  )

  useEffect(() => {
    if (!lenis) return

    function onClassNameChange(lenis) {
      console.log(lenis.className)
    }

    lenis.on('className change', onClassNameChange)

    return () => {
      lenis.off('className change', onClassNameChange)
    }
  }, [lenis])

  useScroll(({ scroll }) => {
    setHasScrolled(scroll > 10)
    if (!zoomWrapperRect.top) return

    const start = zoomWrapperRect.top + windowHeight * 0.5
    const end = zoomWrapperRect.top + zoomWrapperRect.height - windowHeight

    const progress = clamp(0, mapRange(start, end, scroll, 0, 1), 1)
    const center = 0.6
    const progress1 = clamp(0, mapRange(0, center, progress, 0, 1), 1)
    const progress2 = clamp(0, mapRange(center - 0.055, 1, progress, 0, 1), 1)
    setTheme(progress2 === 1 ? 'light' : 'dark')

    zoomRef.current.style.setProperty('--progress1', progress1)
    zoomRef.current.style.setProperty('--progress2', progress2)

    if (progress === 1) {
      zoomRef.current.style.setProperty('background-color', 'currentColor')
    } else {
      zoomRef.current.style.removeProperty('background-color')
    }
  })

  //removed whyRef from the first variable
  const [whyRect] = useRect()
  const [cardsRect] = useRect()
  const [whiteRect] = useRect()
  const [featuresRect] = useRect()
  const [inuseRect] = useRect()

  const addThreshold = useStore(({ addThreshold }) => addThreshold)

  useEffect(() => {
    addThreshold({ id: 'top', value: 0 })
  }, [])

  useEffect(() => {
    const top = whyRect.top - windowHeight / 2
    addThreshold({ id: 'why-start', value: top })
    addThreshold({
      id: 'why-end',
      value: top + whyRect.height,
    })
  }, [whyRect])

  useEffect(() => {
    const top = cardsRect.top - windowHeight / 2
    addThreshold({ id: 'cards-start', value: top })
    addThreshold({ id: 'cards-end', value: top + cardsRect.height })
    addThreshold({
      id: 'red-end',
      value: top + cardsRect.height + windowHeight,
    })
  }, [cardsRect])

  useEffect(() => {
    const top = whiteRect.top - windowHeight
    addThreshold({ id: 'light-start', value: top })
  }, [whiteRect])

  useEffect(() => {
    const top = featuresRect.top
    addThreshold({ id: 'features', value: top })
  }, [featuresRect])

  useEffect(() => {
    const top = inuseRect.top
    addThreshold({ id: 'in-use', value: top })
  }, [inuseRect])

  useEffect(() => {
    const top = lenis?.limit
    addThreshold({ id: 'end', value: top })
  }, [lenis?.limit])

  useScroll((e) => {
    console.log(window.scrollY, e.scroll, e.isScrolling, e.velocity, e.isLocked)
    // console.log(e.isScrolling)
  })

  const inUseRef = useRef()

  const [setIsVisible] = useState(false)
  const intersection = useIntersection(inUseRef, {
    threshold: 0.2,
  })
  useEffect(() => {
    if (intersection?.isIntersecting) {
      setIsVisible(true)
    }
  }, [intersection])

  return (
    <Layout
      theme={theme}
      seo={{
        title: 'Xeno Robotics',
        description:
          'Xeno Robotics is a pioneering engineering firm fusing state-of-the-art robotics with blockchain-driven intelligence to deliver autonomous systems that adapt, learn, and collaborate in diverse environments.',
      }}
      className={s.home}
    >
      <div className={s.canvas}>
        <WebGL />
      </div>

      <section
        className={s.why}
        style={{ marginBottom: '20vh' }}
        // data-lenis-scroll-snap-align="start"
        // ref={whyRef}
      >
        <div className="layout-grid">
          <h2 className={cn(s.sticky, 'h2')}>
            <AppearTitle>Terms and Conditions</AppearTitle>
          </h2>
          <aside className={s.features}>
            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>0. General Provisions</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      0.1 Acceptance of Terms:{' '}
                    </span>
                    By accessing or using the XENO Robotics website, products,
                    or services, you agree to be bound by these Terms and
                    Conditions.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>0.2 Eligibility: </span>
                    Users must be at least 18 years of age or the legal age of
                    majority in their jurisdiction.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      0.3 Modifications:{' '}
                    </span>
                    XENO reserves the right to amend these Terms at its sole
                    discretion. All changes will take immediate effect once
                    posted.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      0.4 Governing Law:{' '}
                    </span>
                    These Terms are governed by and construed in accordance with
                    the laws of Dubai, United Arab Emirates.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>0.5 Contact: </span>
                    For any inquiries, contact{' '}
                    <a
                      style={{ color: '#0097FE' }}
                      href="mailto:support@xeno.ae "
                    >
                      support@xeno.ae
                    </a>
                    .
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>
                1. Accounts and Registration
              </h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      1.1 Account Creation:{' '}
                    </span>
                    Users are required to provide accurate, current, and
                    complete information when registering an account.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      1.2 Security of Credentials:{' '}
                    </span>
                    Users are fully responsible for safeguarding their login
                    credentials and all activities under their accounts.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      1.3 Account Termination:{' '}
                    </span>
                    XENO has the authority to suspend or terminate accounts
                    immediately if these Terms are violated.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      1.4 KYC Requirements :{' '}
                    </span>
                    Know Your Customer (KYC) verification is mandatory for
                    specified services to ensure compliance with regulatory
                    standards.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>2. Purchases and Payments</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      2.1 Accepted Payments:{' '}
                    </span>
                    Payments are accepted in fiat currencies, cryptocurrencies,
                    and XENO tokens.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>2.2 Pricing : </span>
                    All prices are fixed at the time of purchase and subject to
                    adjustment only by XENO.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>2.3 Taxes: </span>
                    Users are fully responsible for the payment of all
                    applicable taxes required by law.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      2.4 Payment Disputes :{' '}
                    </span>
                    All payment disputes must be formally submitted within
                    fourteen (14) days of the transaction date
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>2.5 Refunds : </span>
                    Refunds are processed only under the official company refund
                    policy.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>3. Products and Services</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      3.1 Product Descriptions :{' '}
                    </span>
                    XENO provides accurate product descriptions to the best of
                    its ability, though descriptions are not guaranteed to be
                    without error.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>3.2 Availability: </span>
                    Product availability is not guaranteed and may change
                    without prior notice.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      3.3 Customization:{' '}
                    </span>
                    Customization is subject to technical feasibility and must
                    be approved by XENO.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>3.4 Delivery : </span>
                    Delivery schedules are determined by XENO and confirmed at
                    the time of order.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      3.5 Risk of Loss :{' '}
                    </span>
                    Ownership and risk are transferred to the customer upon
                    confirmed delivery.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>3.6 Maintenance </span>
                    <br />
                    <p>
                      <span style={{ color: '#0097FE' }}>3.6.1: </span>
                      Maintenance contracts are separate from product purchases.
                    </p>
                    <p>
                      <span style={{ color: '#0097FE' }}> 3.6.2: </span>Clients
                      must comply with conditions set forth in maintenance
                      agreements.
                    </p>
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>3.7 Rentals</span>
                    <br />
                    <p>
                      <span style={{ color: '#0097FE' }}>3.7.1: </span>Robots on
                      Rent (ROR) contracts are binding for the agreed duration.
                    </p>
                    <p>
                      <span style={{ color: '#0097FE' }}> 3.7.2: </span>Early
                      termination initiated by the client will result in
                      penalties.
                    </p>
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>4. XENO Token</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>4.1 Utility : </span>
                    The XENO token provides access to discounts, gift card
                    bonuses, rental participation, and governance rights.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>4.2 Taxes : </span>
                    The token will launch with a transaction tax system, which
                    may be revised by XENO. Taxes will be reduced as the
                    ecosystem becomes self-sustaining.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      4.3 Disclaimer of Liability for Trading Losses:{' '}
                    </span>
                    Neither XENO Robotics as a company nor any member of the
                    XENO team shall bear responsibility for financial losses,
                    damages, or claims arising from the trading or holding of
                    the XENO token. All trading activity is undertaken at the
                    user’s own risk.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>4.4 Governance: </span>
                    DAO governance votes are non-binding except where recognized
                    by applicable law.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>5. Intellectual Property</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>5.1 Ownership : </span>
                    All content, logos, trademarks, and software are the
                    exclusive property of XENO Robotics.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      5.2 Limited License :{' '}
                    </span>
                    Users are granted a restricted, non-transferable license to
                    use services solely for personal or approved business
                    purposes.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>5.3 Restrictions: </span>
                    Users are prohibited from copying, distributing, or
                    exploiting XENO intellectual property without express
                    authorization.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>6. Data Privacy</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>6.1 Collection : </span>
                    Data collection is conducted in accordance with the Privacy
                    Policy of XENO Robotics.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>6.2 Usage: </span>
                    Data is utilized exclusively for processing orders,
                    improving services, and meeting regulatory obligations.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>6.3 Sharing: </span>
                    Personal data is not sold and is shared only with authorized
                    service providers or legal authorities.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      6.4 International Transfers :{' '}
                    </span>
                    Data may be transferred internationally if required to
                    fulfill operational obligations.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>
                7. Liability and Disclaimers
              </h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      7.1 Limitation of Liability:{' '}
                    </span>
                    XENO shall not be held liable for indirect, incidental, or
                    consequential damages.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      7.2 Product Risks :{' '}
                    </span>
                    Robotic products involve operational risks, and users accept
                    full responsibility for their operation.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      7.3 Force Majeure :{' '}
                    </span>
                    XENO is not responsible for delays or failures caused by
                    circumstances beyond its reasonable control.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>8. User Responsibilities</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>8.1 Lawful Use : </span>
                    Users are required to use all services strictly in
                    compliance with applicable laws.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>8.2 Misuse : </span>
                    Improper or unauthorized use of XENO products or services
                    will result in immediate termination of access.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>8.3 Compliance: </span>
                    Users must comply with all regional laws relating to
                    robotics, digital assets, and imports.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>9. Dispute Resolution</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      9.1 Governing Jurisdiction :{' '}
                    </span>
                    All disputes will be resolved under the laws of Dubai,
                    United Arab Emirates.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>9.2 Arbitration : </span>
                    Disputes shall be referred to binding arbitration in Dubai.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      9.3 Class Action Waiver:{' '}
                    </span>
                    Users waive the right to initiate or participate in
                    class-action lawsuits.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>10. Termination</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>10.1 By User: </span>
                    Users may close their accounts at any time.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>10.2 By XENO : </span>
                    XENO reserves the right to suspend or terminate accounts at
                    its discretion for breaches of these Terms.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>10.3 Survival: </span>
                    Key provisions including liability, intellectual property,
                    and compliance obligations will remain in effect following
                    termination.
                  </li>
                </ul>
              </p>
            </div>

            <div className={s.feature}>
              <h3 className={cn(s.title, 'h4')}>11. Final Provisions</h3>
              <p className="p">
                <ul>
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      11.1 Entire Agreement :{' '}
                    </span>
                    These Terms represent the full and binding agreement between
                    XENO Robotics and the user.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>
                      11.2 Severability :{' '}
                    </span>
                    If any provision of these Terms is deemed invalid, the
                    remaining provisions shall continue in force.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>11.3 Waiver : </span>
                    Failure by XENO to enforce any provision does not constitute
                    a waiver of that provision.
                  </li>
                  <br />
                  <li>
                    <span style={{ color: '#0097FE' }}>11.4 Assignment : </span>
                    Users are not permitted to assign rights or obligations
                    under these Terms without the prior written consent of XENO
                  </li>
                </ul>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  )
}

export async function getStaticProps() {
  return {
    props: {
      id: 'home',
    }, // will be passed to the page component as props
  }
}
